// Wave 3 — Public Portfolio page (real projects only; honest empty state).
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicShell, { CapabilityTile } from "../../components/public/PublicShell";
import { api } from "../../lib/api";
import { BACKEND } from "../../lib/portfolio";

export default function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [pmProjects, setPmProjects] = useState([]);
  const [params, setParams] = useSearchParams();
  const filter = params.get("service") || "";

  useEffect(() => {
    api.get("/public/website/portfolio").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/public/website/services").then((r) => setServices(r.data)).catch(() => {});
    const site = new URLSearchParams(window.location.search).get("site") || undefined;
    api.get("/public/portfolio-status", { params: { site } })
      .then((r) => (r.data.enabled ? api.get("/public/portfolio/projects", { params: { site } }) : null))
      .then((r) => r && setPmProjects(r.data.items))
      .catch(() => {});
  }, []);

  const filtered = filter ? projects.filter((p) => p.service_slug === filter) : projects;

  const hero = (
    <section className="erf-public-hero" style={{ padding: "72px 0 56px" }} data-testid="portfolio-hero">
      <div className="erf-public-container">
        <div className="erf-public-hero-eyebrow">Portfolio</div>
        <h1 style={{ maxWidth: 800 }}>Real completed projects.</h1>
        <p className="lead">
          Every project on this page is a real completed job. We don't post placeholders or works-in-progress — this list grows as we finish work.
        </p>
      </div>
    </section>
  );

  return (
    <PublicShell hero={hero}>
      <section className="erf-public-section">
        <div className="erf-public-container">
          {projects.length > 0 && services.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 32 }} data-testid="portfolio-filters">
              <button
                className={`erf-public-cta-outline ${!filter ? "" : ""}`}
                onClick={() => setParams({})}
                style={{ background: !filter ? "var(--exec-primary)" : "transparent", color: !filter ? "var(--exec-bg)" : "var(--exec-primary)" }}
                data-testid="filter-all"
              >
                All
              </button>
              {services.filter((s) => projects.some((p) => p.service_slug === s.slug)).map((s) => (
                <button
                  key={s.slug}
                  className="erf-public-cta-outline"
                  onClick={() => setParams({ service: s.slug })}
                  style={{
                    background: filter === s.slug ? "var(--exec-primary)" : "transparent",
                    color: filter === s.slug ? "var(--exec-bg)" : "var(--exec-primary)",
                  }}
                  data-testid={`filter-${s.slug}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          {pmProjects.length > 0 && (
            <div className="erf-public-services-grid" style={{ marginBottom: 32 }} data-testid="portfolio-pm-grid">
              {pmProjects.map((p) => (
                <Link key={p.slug} to={`/portfolio/${p.slug}${window.location.search}`} className="erf-public-service-card" data-testid={`portfolio-pm-card-${p.slug}`}>
                  {p.cover?.urls?.thumb && (
                    <img src={`${BACKEND}${p.cover.urls.thumb}`} alt={p.cover.alt_text || p.title} loading="lazy"
                      style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover" }} />
                  )}
                  <div className="erf-public-service-card-body">
                    <div className="erf-public-service-card-eyebrow">{[p.address_city, p.address_state].filter(Boolean).join(", ")}</div>
                    <h3>{p.title}</h3>
                    <p>{p.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {filtered.length === 0 && pmProjects.length > 0 ? null : filtered.length === 0 ? (
            <div className="erf-public-empty" data-testid="portfolio-empty">
              <h3>We're growing our public portfolio.</h3>
              <p>Recent completed work is available on request during your consultation. Get in touch and we'll walk you through relevant past projects, references, and completed-job documentation.</p>
              <Link to="/quote" className="erf-public-cta">Start a conversation</Link>
            </div>
          ) : (
            <div className="erf-public-services-grid" data-testid="portfolio-grid">
              {filtered.map((p) => (
                <Link key={p.id} to={`/portfolio/${p.id}`} className="erf-public-service-card" data-testid={`portfolio-card-${p.id}`}>
                  <CapabilityTile service={{ slug: p.service_slug, name: p.project_name }} />
                  <div className="erf-public-service-card-body">
                    <div className="erf-public-service-card-eyebrow">{p.city}, {p.state}</div>
                    <h3>{p.project_name}</h3>
                    <p>{p.summary}</p>
                    {((p.photos && p.photos.length > 0) || (p.videos && p.videos.length > 0)) && (
                      <div style={{ marginTop: 8, fontFamily: "monospace", fontSize: 11, color: "var(--exec-muted)" }} data-testid={`portfolio-card-media-${p.id}`}>
                        {p.photos && p.photos.length > 0 && (
                          <span>{p.photos.length} photo{p.photos.length === 1 ? "" : "s"}</span>
                        )}
                        {p.photos && p.photos.length > 0 && p.videos && p.videos.length > 0 && <span> · </span>}
                        {p.videos && p.videos.length > 0 && (
                          <span>{p.videos.length} video{p.videos.length === 1 ? "" : "s"}</span>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicShell>
  );
}
